import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useUser } from "@clerk/react";
import {
  getUserSubscription,
  upsertUserSubscription,
  getSearchCount,
  incrementSearchCount,
  getAnonSearchCount,
  incrementAnonSearchCount,
} from "../utils/supabase";

const SubscriptionContext = createContext(null);

// ── Tier limits ───────────────────────────────────────────────────────────────
export const TIER_LIMITS = {
  free:       { dailySearches: 10, anonSearches: 5, results: 10       },
  pro:        { dailySearches: Infinity, anonSearches: 0, results: 200 },
  enterprise: { dailySearches: Infinity, anonSearches: 0, results: 200 },
};

// ── Year access limits ────────────────────────────────────────────────────────
export const FREE_YEAR_MIN = 2023;

export function getAvailableYears(isPro, allYears) {
  if (isPro) return allYears;
  return Object.fromEntries(
    Object.entries(allYears).filter(([y]) => parseInt(y) >= FREE_YEAR_MIN)
  );
}

// ── Anonymous session key ─────────────────────────────────────────────────────
function getSessionKey() {
  let key = localStorage.getItem("paywatch_session_key");
  if (!key) {
    key = `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("paywatch_session_key", key);
  }
  return key;
}

export function SubscriptionProvider({ children }) {
  const { user, isSignedIn } = useUser();

  const [tier, setTier]                     = useState("free");
  const [searchesUsed, setSearchesUsed]     = useState(0);
  const [anonSearches, setAnonSearches]     = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSignInModal, setShowSignInModal]   = useState(false);
  const [upgradeMessage, setUpgradeMessage]     = useState(null);
  const [upgradeFocusTier, setUpgradeFocusTier] = useState(null); // null = show both tiers; "enterprise" = show Enterprise only
  const [loading, setLoading]               = useState(true);

  // ── Load subscription + search counts on auth change ─────────────────────

  useEffect(() => {
    async function load() {
      setLoading(true);

      if (isSignedIn && user) {
        // Signed-in user — load tier and daily search count from Supabase
        try {
          const sub = await getUserSubscription(user.id);
          if (sub) {
            setTier(sub.tier ?? "free");
            setSearchesUsed(sub.daily_search_count ?? 0);
          } else {
            // No record found by clerk_user_id
            // Check if a subscription exists by email (e.g. user subscribed before signing up)
            const email = user.primaryEmailAddress?.emailAddress;
            const emailSub = await getUserSubscriptionByEmail(email);
            if (emailSub && !emailSub.clerk_user_id) {
              // Patch the clerk_user_id onto the existing subscription
              await upsertUserSubscription(user.id, email, {
                tier: emailSub.tier,
                is_pro: emailSub.is_pro,
                stripe_customer_id: emailSub.stripe_customer_id,
                stripe_subscription_id: emailSub.stripe_subscription_id,
              });
              setTier(emailSub.tier ?? "free");
              setSearchesUsed(0);
            } else {
              // Truly new user — create free record
              await upsertUserSubscription(user.id, email, { tier: "free" });
              setTier("free");
              setSearchesUsed(0);
            }
          }
        } catch {
          setTier("free");
          setSearchesUsed(0);
        }
      } else {
        // Anonymous user — load anon search count from Supabase
        setTier("free");
        try {
          const sessionKey = getSessionKey();
          const { count } = await getAnonSearchCount(sessionKey);
          setAnonSearches(count ?? 0);
        } catch {
          setAnonSearches(0);
        }
      }

      setLoading(false);
    }

    load();
  }, [isSignedIn, user]);

  // ── Derived tier flags ────────────────────────────────────────────────────

  const isPro        = tier === "pro" || tier === "enterprise";
  const isEnterprise = tier === "enterprise";
  const limits       = TIER_LIMITS[tier] ?? TIER_LIMITS.free;

  // ── Result limit for current tier ────────────────────────────────────────

  function getResultLimit() {
    return limits.results;
  }

  // ── Can the user search? ──────────────────────────────────────────────────

  function canSearch() {
    if (isPro) return true;

    if (!isSignedIn) {
      // Anonymous — check anon limit (5)
      return anonSearches < TIER_LIMITS.free.anonSearches;
    }

    // Signed-in — check daily limit
    return searchesUsed < limits.dailySearches;
  }

  // ── Increment search count + gate check ──────────────────────────────────

  async function incrementSearch() {
    if (isPro) return true; // unlimited

    if (!isSignedIn) {
      // Anonymous user
      if (anonSearches >= TIER_LIMITS.free.anonSearches) {
        // Hit anon limit — show sign-in modal
        setShowSignInModal(true);
        return false;
      }
      try {
        const sessionKey = getSessionKey();
        // Update local state immediately for responsive UI
        const newCount = anonSearches + 1;
        setAnonSearches(newCount);
        // Persist to Supabase in background
        incrementAnonSearchCount(sessionKey).catch(() => {});

        if (newCount >= TIER_LIMITS.free.anonSearches) {
          setShowSignInModal(true);
          return false;
        }
      } catch {
        setAnonSearches((prev) => prev + 1);
      }
      return true;      
    }

    // Signed-in user
    if (searchesUsed >= limits.dailySearches) {
      setUpgradeMessage(
        "You've used all 10 of your free daily searches. Upgrade to Pro for unlimited searches and full access."
      );
      setShowUpgradeModal(true);
      return false;
    }

    try {
      const { count } = await incrementSearchCount(user.id);
      setSearchesUsed(count);

      // After incrementing, check if now at limit
      if (count >= limits.dailySearches) {
        setUpgradeMessage(
          "You've used all 10 of your free daily searches. Upgrade to Pro for unlimited searches and full access."
        );
        setShowUpgradeModal(true);
        return false;
      }
    } catch {
      setSearchesUsed((prev) => prev + 1);
    }

    return true;
  }

  // ── Stripe Checkout ───────────────────────────────────────────────────────

  async function upgradeToTier(selectedTier) {
    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: selectedTier }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    }
  }

  // Keep upgradeToPro as an alias for backward compatibility
  async function upgradeToPro() {
    return upgradeToTier("pro");
  }

  // ── Searches remaining display ────────────────────────────────────────────

  function searchesRemaining() {
    if (isPro) return Infinity;
    if (!isSignedIn) return Math.max(0, TIER_LIMITS.free.anonSearches - anonSearches);
    return Math.max(0, limits.dailySearches - searchesUsed);
  }

  return (
    <SubscriptionContext.Provider
      value={{
        tier,
        setTier,
        isPro,
        isEnterprise,
        searchesUsed,
        anonSearches,
        loading,
        canSearch,
        incrementSearch,
        upgradeToTier,
        upgradeToPro,
        getResultLimit,
        showUpgradeModal,
        setShowUpgradeModal,
        showSignInModal,
        setShowSignInModal,
        upgradeMessage,
        setUpgradeMessage,
        upgradeFocusTier,
        setUpgradeFocusTier,
        searchesRemaining: searchesRemaining(),
        limits,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}